import { useCampaignCRUDActions } from '@mm/state/actions/CampaignsCRUDActions';
import {
  CampaignType,
  SoldConfigurationsType,
} from '@mm/state/model/CampaignsModel';
import { selectCampaignById } from '@mm/state/selectors/campaignSelectors';
import {
  activeChatbotIdSelector,
  activeFacebookPageIdSelector,
} from '@mm/state/selectors/viewSelectors';
import { useFormik } from 'formik';
import { cloneDeep, isEqual } from 'lodash';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { errorToast, successToast } from '../../../../../common/helpers/toast';
import { useSlideoutOnClose } from '../SlideoutEmail/SlideoutEmail';
import { useSlackCRUDActions } from '@mm/state/actions/SlackCRUDActions';
import SOLDSlackHelper from './schema-helper';

import { soldConfigInitialValues } from '../AutomationSoLDSettingsSlideout/AutomationSoLDSettingsSlideout';
import { SlideOutMenuType } from '@mm/state/interfaces/view';
import { SlackNotificationEditor } from '../SlackNotificationSlideoutEditor/SlackNotificationEditor/SlackNotificationEditor';

export type IAutomationSoLDSlackNotificationSettings = {
  menuType: SlideOutMenuType;
  campaignId?: number;
  onClose?: () => void;
};

export const AutomationSoLDSlackNotificationSettings: React.FC<
  IAutomationSoLDSlackNotificationSettings
> = props => {
  const { menuType, campaignId = -1, onClose: onCloseProp = () => {} } = props;

  const chatbotId = useSelector(activeChatbotIdSelector);
  const campaign = useSelector(state => selectCampaignById(state, campaignId));
  const activeFbPageId = useSelector(activeFacebookPageIdSelector);
  const campaignCRUDActions = useCampaignCRUDActions();
  const [busy, setBusy] = useState(false);
  const [isTestSuccess, setIsTestSuccess] = useState<boolean | undefined>(
    undefined,
  );
  const { sendTest } = useSlackCRUDActions();

  const initials = cloneDeep(
    campaign?.soldConfigurations || soldConfigInitialValues,
  ) as SoldConfigurationsType;
  if (!campaign?.soldConfigurations?.unicorn.notifications.slack) {
    initials.unicorn.notifications.slack = {
      ...soldConfigInitialValues.unicorn.notifications.slack,
    };
  }

  const formik = useFormik<SoldConfigurationsType>({
    initialValues: initials,
    validate: SOLDSlackHelper.validate(),
    onSubmit: async () => {},
  });

  const hasChanged = !isEqual(
    campaign?.soldConfigurations?.unicorn?.notifications?.slack,
    formik.values.unicorn.notifications?.slack,
  );

  const setField = (fieldName: string, value: any) => {
    formik.setFieldValue(`unicorn.notifications.slack.${fieldName}`, value);
  };
  const getField = (fieldName: string) =>
    formik.values?.unicorn?.notifications?.slack?.[fieldName];

  const onSave = async (skipClose?: boolean) => {
    setBusy(true);
    try {
      if (campaignId) {
        await campaignCRUDActions.updateCampaign(
          {
            id: campaignId,
            soldConfigurations: formik.values,
          } as unknown as CampaignType,
          chatbotId,
        );

        successToast('Your changes have been saved.', 'top-left');

        if (!skipClose) {
          closeSlideout();
        }
      }
    } catch (ee) {
      errorToast('Something went wrong', 'top-left');
    } finally {
      setBusy(false);
    }
  };

  const test = async () => {
    try {
      if (hasChanged) {
        await onSave(true);
      }
      const res = await sendTest(
        activeFbPageId,
        formik.values.unicorn.notifications.slack,
      );
      if (res.success) {
        setIsTestSuccess(true);
      }
    } catch (e) {
      setIsTestSuccess(false);
    }
  };

  const onClose = () =>
    onBeforeClose(
      campaign?.soldConfigurations?.unicorn?.notifications?.slack,
      formik.values.unicorn.notifications?.slack,
    );

  const close = async () => {
    try {
      await onClose();
      closeSlideout();
      onCloseProp();
    } catch (err) {}
  };

  const {
    setRef,
    close: closeSlideout,
    onBeforeClose,
  } = useSlideoutOnClose(onClose, menuType);

  return (
    <SlackNotificationEditor
      formik={formik}
      setField={setField}
      getField={getField}
      onSave={onSave}
      test={test}
      close={close}
      busy={busy}
      setRef={setRef}
      isTestSuccess={isTestSuccess}
      setIsTestSuccess={setIsTestSuccess}
      showBackButton={true}
    />
  );
};
